import {
  FORM_FIELD_CAPABILITY_KEYS,
  FORM_FIELD_TYPES,
  canCreateFormField,
  formFieldRegistration,
  type FormFieldType,
} from '../../../../src/contracts/form-field-registry.js';
import type { FormField, Locale } from '../../types.js';

export { FORM_FIELD_CAPABILITY_KEYS, FORM_FIELD_TYPES, type FormFieldType };

type FieldOf<Type extends FormFieldType> = Extract<FormField, { type: Type }>;
type FieldFactoryMap = { [Type in FormFieldType]: (locale: Locale) => FieldOf<Type> };

function translated(locale: Locale, en: string, zh: string): string {
  return locale === 'zh-CN' ? zh : en;
}

function base<Type extends FormFieldType>(type: Type, locale: Locale): {
  id: string;
  type: Type;
  title: string;
  required: false;
} {
  const label = formFieldRegistration(type).label;
  return { id: crypto.randomUUID(), type, title: translated(locale, label.en, label.zh), required: false };
}

function choiceOptions(locale: Locale) {
  return [
    { id: crypto.randomUUID(), label: translated(locale, 'Option 1', '选项 1') },
    { id: crypto.randomUUID(), label: translated(locale, 'Option 2', '选项 2') },
  ];
}

const FIELD_FACTORIES = {
  text: (locale) => ({ ...base('text', locale), title: translated(locale, 'Question', '问题'), maxLength: 200 }),
  phone: (locale) => base('phone', locale),
  singleChoice: (locale) => ({ ...base('singleChoice', locale), title: translated(locale, 'Choose one', '请选择一项'), options: choiceOptions(locale) }),
  multilineText: (locale) => ({ ...base('multilineText', locale), title: translated(locale, 'Details', '详细说明'), maxLength: 1_000 }),
  number: (locale) => ({ ...base('number', locale), mode: 'decimal' as const }),
  date: (locale) => base('date', locale),
  time: (locale) => base('time', locale),
  multiChoice: (locale) => ({ ...base('multiChoice', locale), title: translated(locale, 'Choose all that apply', '请选择所有符合项'), options: choiceOptions(locale), minSelect: 1, maxSelect: 2 }),
  cascadingSelect: (locale) => ({
    ...base('cascadingSelect', locale),
    title: translated(locale, 'Choose a path', '请选择路径'),
    options: [
      { id: crypto.randomUUID(), label: translated(locale, 'Region A', '区域 A'), children: [{ id: crypto.randomUUID(), label: translated(locale, 'Area 1', '片区 1') }] },
      { id: crypto.randomUUID(), label: translated(locale, 'Region B', '区域 B'), children: [{ id: crypto.randomUUID(), label: translated(locale, 'Area 2', '片区 2') }] },
    ],
  }),
  checklist: (locale) => ({
    ...base('checklist', locale),
    items: [{ id: crypto.randomUUID(), label: translated(locale, 'New checklist item', '新检查项') }],
    passLabel: translated(locale, 'Pass', '合格'),
    failLabel: translated(locale, 'Fail', '异常'),
    requireNoteOnFail: false,
    allowResultImages: false,
    maxResultImages: 3,
  }),
  image: (locale) => ({ ...base('image', locale), minImages: 0, maxImages: 3, capturePreference: 'any' as const, cameraFacing: 'environment' as const }),
  file: (locale) => ({ ...base('file', locale), minFiles: 0, maxFiles: 3 }),
  signature: (locale) => base('signature', locale),
  location: (locale) => base('location', locale),
  address: (locale) => base('address', locale),
  sectionTitle: (locale) => ({ ...base('sectionTitle', locale), title: translated(locale, 'New section', '新分节'), required: false as const }),
} satisfies FieldFactoryMap;

export function createFormField<Type extends FormFieldType>(type: Type, locale: Locale): FieldOf<Type> {
  return FIELD_FACTORIES[type](locale) as FieldOf<Type>;
}

export function formFieldTypeLabel(type: FormFieldType, locale: Locale): string {
  const label = formFieldRegistration(type).label;
  return translated(locale, label.en, label.zh);
}

export function availableFormFieldTypes(capabilities: Iterable<string>): FormFieldType[] {
  return FORM_FIELD_TYPES.filter((type) => canCreateFormField(type, capabilities));
}

export function isFormFieldAvailable(type: FormFieldType, capabilities: Iterable<string>): boolean {
  return canCreateFormField(type, capabilities);
}

export function unavailableFormFieldTypes(fields: readonly FormField[], capabilities: Iterable<string>): FormFieldType[] {
  const unavailable = new Set<FormFieldType>();
  for (const field of fields) {
    if (!canCreateFormField(field.type, capabilities)) unavailable.add(field.type);
  }
  return [...unavailable];
}
